import { Link } from 'react-router-dom';
import {
  Text,
  SimpleGrid,
  Paper,
  Group,
  Stack,
  Skeleton,
  Button,
  Badge,
  ThemeIcon,
  Title,
  Box,
  Center,
  Tooltip,
} from '@mantine/core';
import {
  IconShoppingCart,
  IconTag,
  IconStar,
  IconRefresh,
  IconShoppingCartPlus,
  IconTruck,
  IconClipboardList,
  IconArrowRight,
  IconPackage,
  IconCalendarEvent,
  IconClock,
  IconLeaf,
  IconAlertTriangle,
  IconFlame,
  IconListCheck,
  IconCategory,
  IconSparkles,
  IconRepeat,
  IconTemplate,
  IconChartBar,
  IconSearch,
  IconMapPin,
  IconGps,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import StatusBadge from '@/components/StatusBadge';
import EmptyState from '@/components/EmptyState';
import ProductCard from '@/components/ProductCard';
import { useAuthStore } from '@/store/auth';
import {
  useSpendingSummary,
  useOrders,
  usePromotions,
  useFeaturedProducts,
  useSuggestedOrder,
  useAddToCart,
  useShortageAlerts,
  useRecentOrderItems,
  useTracking,
  useLiveTracking,
} from '@/hooks/usePortalApi';
import dayjs from 'dayjs';

export default function Dashboard() {
  const user = useAuthStore((s) => s.user);
  const { data: spending, isLoading: spendingLoading } = useSpendingSummary();
  const { data: ordersData, isLoading: ordersLoading } = useOrders({ limit: 5 });
  const { data: promos } = usePromotions();
  const { data: featured, isLoading: featuredLoading } = useFeaturedProducts();
  const { data: suggested } = useSuggestedOrder();
  const addToCart = useAddToCart();
  const { data: shortageData } = useShortageAlerts('active');
  const { data: recentItemsData } = useRecentOrderItems(8);

  const nextDeliveryOrder = ordersData?.items?.find((o) => o.status === 'shipped')
    || ordersData?.items?.find((o) => o.status === 'packed')
    || ordersData?.items?.[0] || null;
  const nextDeliveryId = nextDeliveryOrder?.id ?? 0;

  const { data: nextTracking } = useTracking(nextDeliveryId);
  const { data: nextLive } = useLiveTracking(nextDeliveryId);

  const displayName = user?.display_name || user?.customer?.name || user?.username || 'Customer';

  const suggestedItems = suggested?.suggested_items?.slice(0, 6) ?? [];
  const shortageAlerts = (shortageData as any)?.alerts ?? [];
  const recentItems = (recentItemsData as any)?.items?.slice(0, 6) ?? [];

  const handleAddAllSuggested = () => {
    suggestedItems.forEach((item) => {
      addToCart.mutate({ product_id: item.product_id, quantity: item.suggested_qty });
    });
    notifications.show({
      title: 'Added to cart',
      message: `${suggestedItems.length} suggested items added`,
      color: 'brand',
    });
  };

  const activeOrders = ordersData?.items?.filter(
    (o) => o.status !== 'delivered' && o.status !== 'cancelled',
  ) ?? [];

  return (
    <Stack gap="lg">
      {/* Hero Welcome Banner */}
      <Paper
        p="xl"
        radius="lg"
        style={{
          background: 'linear-gradient(135deg, var(--mantine-color-brand-8) 0%, var(--mantine-color-brand-6) 100%)',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box
          style={{
            position: 'absolute',
            top: -30,
            right: -30,
            opacity: 0.08,
          }}
        >
          <IconLeaf size={200} />
        </Box>
        <Stack gap="md" style={{ position: 'relative', zIndex: 1 }}>
          <Group gap="xl" align="center">
            <img
              src="/logo.png"
              alt="Standard Produce Co."
              style={{
                height: 160,
                filter: 'brightness(0) invert(1)',
                opacity: 0.9,
              }}
            />
            <div style={{ flex: 1 }}>
              <Text size="sm" c="rgba(255,255,255,0.7)" fw={500}>
                {dayjs().format('dddd, MMMM D')}
              </Text>
              <Title order={2} c="white" fw={700} mt={4}>
                Welcome back, {displayName}
              </Title>
              <Text size="sm" c="rgba(255,255,255,0.8)" mt={4}>
                Ready to place your next order? Browse our fresh selection or reorder your favorites.
              </Text>
              <Group gap="sm" mt="md">
                <Button
                  component={Link}
                  to="/shop"
                  size="md"
                  variant="white"
                  color="brand"
                  leftSection={<IconShoppingCart size={18} />}
                  rightSection={<IconArrowRight size={16} />}
                >
                  Shop Now
                </Button>
                <Button
                  component={Link}
                  to="/orders/quick-reorder"
                  size="md"
                  variant="outline"
                  color="white"
                  leftSection={<IconRefresh size={18} />}
                  style={{ borderColor: 'rgba(255,255,255,0.4)' }}
                >
                  Quick Reorder
                </Button>
                <Button
                  component={Link}
                  to="/orders"
                  size="md"
                  variant="subtle"
                  color="white"
                  leftSection={<IconClipboardList size={18} />}
                >
                  My Orders
                </Button>
              </Group>
            </div>
          </Group>
        </Stack>
      </Paper>

      {/* Quick Stats Row */}
      <SimpleGrid cols={{ base: 2, sm: 4 }}>
        <Paper
          component={Link}
          to="/orders"
          withBorder
          p="md"
          radius="md"
          style={{
            borderLeft: '3px solid var(--mantine-color-brand-5)',
            textDecoration: 'none',
            cursor: 'pointer',
            transition: 'all 150ms ease',
          }}
          onMouseEnter={(e: React.MouseEvent<HTMLAnchorElement>) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = 'var(--mantine-shadow-md)';
            e.currentTarget.style.backgroundColor = 'var(--mantine-color-brand-0)';
          }}
          onMouseLeave={(e: React.MouseEvent<HTMLAnchorElement>) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '';
            e.currentTarget.style.backgroundColor = '';
          }}
        >
          <Group gap="sm" justify="space-between">
            <Group gap="sm">
              <ThemeIcon variant="light" size={40} radius="xl" color="brand">
                <IconPackage size={20} />
              </ThemeIcon>
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Active Orders</Text>
                <Text fw={800} size="xl" lh={1.2}>{activeOrders.length}</Text>
              </div>
            </Group>
            <IconArrowRight size={16} color="var(--mantine-color-dimmed)" />
          </Group>
        </Paper>
        {nextDeliveryOrder ? (
          <Paper
            component={Link}
            to={`/orders/${nextDeliveryOrder.id}/track`}
            withBorder
            p="md"
            radius="md"
            style={{
              borderLeft: '3px solid var(--mantine-color-produce-5)',
              textDecoration: 'none',
              cursor: 'pointer',
              transition: 'all 150ms ease',
            }}
            onMouseEnter={(e: React.MouseEvent<HTMLAnchorElement>) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = 'var(--mantine-shadow-md)';
              e.currentTarget.style.backgroundColor = 'var(--mantine-color-produce-0)';
            }}
            onMouseLeave={(e: React.MouseEvent<HTMLAnchorElement>) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '';
              e.currentTarget.style.backgroundColor = '';
            }}
          >
            <Group gap="sm" wrap="nowrap" justify="space-between">
              <Group gap="sm" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
                <ThemeIcon variant="light" size={40} radius="xl" color="produce">
                  <IconTruck size={20} />
                </ThemeIcon>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Group gap={6} align="center">
                    <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Next Delivery</Text>
                    {nextLive?.location && (
                      <Tooltip label="Live GPS tracking active">
                        <ThemeIcon variant="light" size={16} radius="xl" color="green">
                          <IconGps size={10} />
                        </ThemeIcon>
                      </Tooltip>
                    )}
                  </Group>
                  <Group gap={6} align="center" mt={2}>
                    <Text fw={700} size="sm" c="brand" lineClamp={1}>
                      {nextDeliveryOrder.order_number}
                    </Text>
                    <StatusBadge status={nextTracking?.status ?? nextDeliveryOrder.status} />
                  </Group>
                  {(nextLive?.eta || nextTracking?.estimated_delivery) && (
                    <Group gap={4} mt={2}>
                      <IconClock size={12} color="var(--mantine-color-dimmed)" />
                      <Text size="xs" c="dimmed">
                        ETA {dayjs(nextLive?.eta ?? nextTracking?.estimated_delivery).format('MMM D, h:mm A')}
                      </Text>
                    </Group>
                  )}
                  {nextTracking?.events?.[0] && (
                    <Group gap={4} mt={2}>
                      <IconMapPin size={12} color="var(--mantine-color-dimmed)" />
                      <Text size="xs" c="dimmed" lineClamp={1}>
                        {nextTracking.events[0].description}
                      </Text>
                    </Group>
                  )}
                </div>
              </Group>
              <IconArrowRight size={16} color="var(--mantine-color-dimmed)" />
            </Group>
          </Paper>
        ) : (
          <Paper
            component={Link}
            to="/delivery/calendar"
            withBorder
            p="md"
            radius="md"
            style={{
              borderLeft: '3px solid var(--mantine-color-produce-5)',
              textDecoration: 'none',
              cursor: 'pointer',
              transition: 'all 150ms ease',
            }}
            onMouseEnter={(e: React.MouseEvent<HTMLAnchorElement>) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = 'var(--mantine-shadow-md)';
              e.currentTarget.style.backgroundColor = 'var(--mantine-color-produce-0)';
            }}
            onMouseLeave={(e: React.MouseEvent<HTMLAnchorElement>) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '';
              e.currentTarget.style.backgroundColor = '';
            }}
          >
            <Group gap="sm" justify="space-between">
              <Group gap="sm">
                <ThemeIcon variant="light" size={40} radius="xl" color="produce">
                  <IconTruck size={20} />
                </ThemeIcon>
                <div>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Next Delivery</Text>
                  <Text fw={800} size="xl" lh={1.2}>--</Text>
                </div>
              </Group>
              <IconArrowRight size={16} color="var(--mantine-color-dimmed)" />
            </Group>
          </Paper>
        )}
        <Paper
          component={Link}
          to="/billing/statement"
          withBorder
          p="md"
          radius="md"
          style={{
            borderLeft: '3px solid var(--mantine-color-amber-5)',
            textDecoration: 'none',
            cursor: 'pointer',
            transition: 'all 150ms ease',
          }}
          onMouseEnter={(e: React.MouseEvent<HTMLAnchorElement>) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = 'var(--mantine-shadow-md)';
            e.currentTarget.style.backgroundColor = 'var(--mantine-color-amber-0)';
          }}
          onMouseLeave={(e: React.MouseEvent<HTMLAnchorElement>) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '';
            e.currentTarget.style.backgroundColor = '';
          }}
        >
          <Group gap="sm" justify="space-between">
            <Group gap="sm">
              <ThemeIcon variant="light" size={40} radius="xl" color="amber">
                <IconCalendarEvent size={20} />
              </ThemeIcon>
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={700}>This Month</Text>
                <Text fw={800} size="xl" lh={1.2}>
                  {spendingLoading ? '--' : `$${((spending?.total_30d ?? 0) / 1000).toFixed(1)}k`}
                </Text>
              </div>
            </Group>
            <IconArrowRight size={16} color="var(--mantine-color-dimmed)" />
          </Group>
        </Paper>
        <Paper
          component={Link}
          to="/delivery/schedule"
          withBorder
          p="md"
          radius="md"
          style={{
            borderLeft: '3px solid var(--mantine-color-blue-5)',
            textDecoration: 'none',
            cursor: 'pointer',
            transition: 'all 150ms ease',
          }}
          onMouseEnter={(e: React.MouseEvent<HTMLAnchorElement>) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = 'var(--mantine-shadow-md)';
            e.currentTarget.style.backgroundColor = 'var(--mantine-color-blue-0)';
          }}
          onMouseLeave={(e: React.MouseEvent<HTMLAnchorElement>) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '';
            e.currentTarget.style.backgroundColor = '';
          }}
        >
          <Group gap="sm" justify="space-between">
            <Group gap="sm">
              <ThemeIcon variant="light" size={40} radius="xl" color="blue">
                <IconSearch size={20} />
              </ThemeIcon>
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Where's My Order?</Text>
                <Text fw={800} size="sm" lh={1.2} c="blue">Track Deliveries</Text>
              </div>
            </Group>
            <IconArrowRight size={16} color="var(--mantine-color-dimmed)" />
          </Group>
        </Paper>
      </SimpleGrid>

      {/* Ways to Order */}
      <Paper p="lg" radius="md">
        <Group justify="space-between" mb="md">
          <Group gap="xs">
            <ThemeIcon variant="light" size="md" radius="xl" color="brand">
              <IconShoppingCart size={18} />
            </ThemeIcon>
            <Text fw={700} size="lg">Ways to Order</Text>
          </Group>
        </Group>
        <SimpleGrid cols={{ base: 2, sm: 3, lg: 6 }}>
          {([
            {
              icon: <IconSearch size={24} />,
              color: 'brand',
              title: 'Browse Catalog',
              desc: 'Search & filter our full product line',
              to: '/shop',
            },
            {
              icon: <IconRefresh size={24} />,
              color: 'produce',
              title: 'Quick Reorder',
              desc: 'One-click reorder from past purchases',
              to: '/orders/quick-reorder',
            },
            {
              icon: <IconSparkles size={24} />,
              color: 'violet',
              title: 'Smart Reorder',
              desc: 'AI-powered suggestions based on your patterns',
              to: '/orders/smart-reorder',
            },
            {
              icon: <IconTemplate size={24} />,
              color: 'amber',
              title: 'Order Templates',
              desc: 'Save preset lists, place with one click',
              to: '/orders/templates',
            },
            {
              icon: <IconRepeat size={24} />,
              color: 'cyan',
              title: 'Recurring Orders',
              desc: 'Automate weekly or monthly orders',
              to: '/orders/recurring',
            },
            {
              icon: <IconChartBar size={24} />,
              color: 'red',
              title: 'Par Levels',
              desc: 'Auto-reorder when stock drops below min',
              to: '/orders/par-levels',
            },
          ] as const).map((item) => (
            <Paper
              key={item.to}
              component={Link}
              to={item.to}
              withBorder
              p="md"
              radius="md"
              style={{
                textDecoration: 'none',
                cursor: 'pointer',
                transition: 'all 150ms ease',
                textAlign: 'center',
              }}
              onMouseEnter={(e: React.MouseEvent<HTMLAnchorElement>) => {
                e.currentTarget.style.transform = 'translateY(-3px)';
                e.currentTarget.style.boxShadow = 'var(--mantine-shadow-md)';
              }}
              onMouseLeave={(e: React.MouseEvent<HTMLAnchorElement>) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '';
              }}
            >
              <Stack align="center" gap="xs">
                <ThemeIcon variant="light" size={48} radius="xl" color={item.color}>
                  {item.icon}
                </ThemeIcon>
                <Text fw={600} size="sm">{item.title}</Text>
                <Text size="xs" c="dimmed" lineClamp={2}>{item.desc}</Text>
              </Stack>
            </Paper>
          ))}
        </SimpleGrid>
      </Paper>

      {/* Low Stock / Shortage Alerts — urgency banner */}
      {shortageAlerts.length > 0 && (
        <Paper
          p="lg"
          radius="md"
          style={{
            background: 'linear-gradient(135deg, #fff5f5 0%, #ffe8e8 100%)',
            border: '1px solid var(--mantine-color-red-2)',
          }}
        >
          <Group mb="md">
            <ThemeIcon variant="filled" size="lg" radius="xl" color="red">
              <IconAlertTriangle size={20} />
            </ThemeIcon>
            <div>
              <Text fw={700} size="lg" c="red.8">Limited Availability</Text>
              <Text size="sm" c="dimmed">These items are running low — order now before they're gone</Text>
            </div>
          </Group>
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
            {shortageAlerts.slice(0, 3).map((alert: any) => (
              <Paper key={alert.id} withBorder p="sm" radius="md" bg="white" shadow="xs">
                <Group justify="space-between" align="start">
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Text fw={600} size="sm" lineClamp={1}>
                      {alert.product_name || 'Item'}
                    </Text>
                    <Text size="xs" c="dimmed">
                      Only {alert.available_qty} left in stock
                    </Text>
                    <Badge variant="light" color="red" size="xs" mt={4}>
                      Low Stock
                    </Badge>
                  </div>
                  <Button
                    size="compact-sm"
                    color="red"
                    variant="filled"
                    leftSection={<IconShoppingCartPlus size={14} />}
                    component={Link}
                    to="/shop"
                  >
                    Order
                  </Button>
                </Group>
              </Paper>
            ))}
          </SimpleGrid>
        </Paper>
      )}

      {/* Promotions / Today's Specials */}
      <Paper
        p="lg"
        radius="md"
        style={{
          background: 'linear-gradient(135deg, var(--mantine-color-amber-0) 0%, #fff8e8 100%)',
          border: '1px solid var(--mantine-color-amber-2)',
        }}
      >
        <Group justify="space-between" mb="md">
          <Group gap="sm">
            <ThemeIcon variant="filled" size="lg" radius="xl" color="amber">
              <IconTag size={20} />
            </ThemeIcon>
            <div>
              <Text fw={700} size="lg">Today's Specials & Deals</Text>
              <Text size="sm" c="dimmed">Exclusive savings for your account</Text>
            </div>
          </Group>
          <Button
            component={Link}
            to="/shop"
            variant="light"
            size="sm"
            color="amber"
            rightSection={<IconArrowRight size={14} />}
          >
            Shop All Deals
          </Button>
        </Group>
        {promos && promos.length > 0 ? (
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
            {promos.map((p) => (
              <Paper key={p.id} withBorder p="sm" radius="md" bg="white" shadow="xs">
                <Group justify="space-between" align="start">
                  <div style={{ flex: 1 }}>
                    <Badge variant="filled" color="amber" size="lg" mb={4}>
                      {p.code}
                    </Badge>
                    <Text size="sm" mt={4}>
                      {p.description}
                    </Text>
                  </div>
                  <Text fw={800} c="amber.8" size="xl">
                    {p.discount_type === 'percentage'
                      ? `${p.discount_value}% OFF`
                      : `$${p.discount_value} OFF`}
                  </Text>
                </Group>
                <Text size="xs" c="dimmed" mt={4}>
                  Expires {dayjs(p.end_date).format('MMM D, YYYY')}
                </Text>
              </Paper>
            ))}
          </SimpleGrid>
        ) : (
          <Center py="md">
            <Stack align="center" gap={4}>
              <Text c="dimmed" size="sm">No active promotions right now — check back soon!</Text>
              <Button component={Link} to="/shop" variant="subtle" color="amber" size="sm">
                Browse Full Catalog
              </Button>
            </Stack>
          </Center>
        )}
      </Paper>

      {/* Featured Products — always visible */}
      <Paper p="lg" radius="md">
        <Group justify="space-between" mb="md">
          <Group gap="xs">
            <ThemeIcon variant="light" size="md" radius="xl" color="produce">
              <IconStar size={18} />
            </ThemeIcon>
            <Text fw={700} size="lg">Featured Products</Text>
          </Group>
          <Button
            component={Link}
            to="/shop"
            variant="light"
            size="sm"
            rightSection={<IconArrowRight size={14} />}
          >
            Browse Catalog
          </Button>
        </Group>
        {featuredLoading ? (
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }}>
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} height={180} radius="md" />
            ))}
          </SimpleGrid>
        ) : featured && featured.length > 0 ? (
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }}>
            {featured.slice(0, 4).map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </SimpleGrid>
        ) : (
          <EmptyState title="No featured products" description="Check back soon for featured items" />
        )}
      </Paper>

      {/* Your Most Ordered — always visible */}
      <Paper
        p="lg"
        radius="md"
        style={{
          background: 'linear-gradient(135deg, var(--mantine-color-brand-0) 0%, var(--mantine-color-white) 100%)',
          border: '1px solid var(--mantine-color-brand-1)',
        }}
      >
        <Group justify="space-between" mb="md">
          <Group gap="xs">
            <ThemeIcon variant="light" size="md" radius="xl" color="brand">
              <IconFlame size={18} />
            </ThemeIcon>
            <div>
              <Text fw={700} size="lg">Your Most Ordered</Text>
              <Text size="xs" c="dimmed">Items you order frequently — one-click reorder</Text>
            </div>
          </Group>
          <Button
            component={Link}
            to="/orders/quick-reorder"
            variant="light"
            size="sm"
            rightSection={<IconArrowRight size={14} />}
          >
            Full Reorder List
          </Button>
        </Group>
        {recentItems.length > 0 ? (
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
            {recentItems.map((item: any) => (
              <Paper key={item.id ?? item.product_id} withBorder p="sm" radius="md" bg="white" shadow="xs">
                <Group justify="space-between" align="start">
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Text fw={600} size="sm" lineClamp={1}>
                      {item.product_name}
                    </Text>
                    <Text size="xs" c="dimmed">{item.sku}</Text>
                    <Group gap={4} mt={4}>
                      <Badge variant="light" color="brand" size="xs">
                        Ordered {item.order_count}x
                      </Badge>
                      <Text size="xs" c="dimmed">
                        Last: {dayjs(item.last_ordered_date).format('MMM D')}
                      </Text>
                    </Group>
                  </div>
                  <Stack gap={2} align="flex-end">
                    <Text fw={700} c="brand" size="sm">
                      ${parseFloat(item.unit_price).toFixed(2)}
                    </Text>
                    <Button
                      size="compact-xs"
                      variant="light"
                      leftSection={<IconShoppingCartPlus size={12} />}
                      onClick={() => {
                        addToCart.mutate({
                          product_id: item.product_id,
                          quantity: item.last_quantity || 1,
                        });
                        notifications.show({
                          title: 'Added to cart',
                          message: `${item.last_quantity || 1}x ${item.product_name}`,
                          color: 'brand',
                        });
                      }}
                    >
                      Reorder
                    </Button>
                  </Stack>
                </Group>
              </Paper>
            ))}
          </SimpleGrid>
        ) : (
          <Center py="lg">
            <Stack align="center" gap="xs">
              <ThemeIcon variant="light" size={48} radius="xl" color="brand">
                <IconCategory size={24} />
              </ThemeIcon>
              <Text fw={600}>Start building your favorites</Text>
              <Text c="dimmed" size="sm" ta="center" maw={300}>
                Place your first order and we'll remember your items for easy one-click reordering.
              </Text>
              <Button component={Link} to="/shop" variant="light" mt="xs" leftSection={<IconShoppingCart size={16} />}>
                Browse Products
              </Button>
            </Stack>
          </Center>
        )}
      </Paper>

      {/* Suggested Reorder (AI-powered) — always visible */}
      <Paper
        p="lg"
        radius="md"
        style={{
          background: 'linear-gradient(135deg, var(--mantine-color-produce-0) 0%, #f0faf0 100%)',
          border: '1px solid var(--mantine-color-produce-2)',
        }}
      >
        <Group justify="space-between" mb="md">
          <Group gap="xs">
            <ThemeIcon variant="light" size="md" radius="xl" color="produce">
              <IconRefresh size={18} />
            </ThemeIcon>
            <div>
              <Text fw={700} size="lg">Suggested for You</Text>
              <Text size="xs" c="dimmed">Smart recommendations based on your ordering patterns</Text>
            </div>
          </Group>
          {suggestedItems.length > 0 && (
            <Button
              size="sm"
              color="produce"
              leftSection={<IconShoppingCartPlus size={16} />}
              onClick={handleAddAllSuggested}
              loading={addToCart.isPending}
            >
              Add All to Cart
            </Button>
          )}
        </Group>
        {suggestedItems.length > 0 ? (
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
            {suggestedItems.map((item) => (
              <Paper key={item.product_id} withBorder p="sm" radius="md" bg="white" shadow="xs">
                <Group justify="space-between" align="start">
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Text fw={600} size="sm" lineClamp={1}>
                      {item.product_name}
                    </Text>
                    <Text size="xs" c="dimmed">{item.sku}</Text>
                    <Badge variant="light" color="produce" size="xs" mt={4}>
                      {item.reason}
                    </Badge>
                  </div>
                  <Stack gap={2} align="flex-end">
                    <Text fw={700} c="produce.7" size="lg">
                      {item.suggested_qty}x
                    </Text>
                    <Text size="xs" c="dimmed">
                      ${Number(item.unit_price).toFixed(2)}/ea
                    </Text>
                    <Button
                      size="compact-xs"
                      variant="light"
                      color="produce"
                      leftSection={<IconShoppingCartPlus size={12} />}
                      onClick={() => {
                        addToCart.mutate({ product_id: item.product_id, quantity: item.suggested_qty });
                        notifications.show({
                          title: 'Added to cart',
                          message: `${item.suggested_qty}x ${item.product_name}`,
                          color: 'brand',
                        });
                      }}
                    >
                      Add
                    </Button>
                  </Stack>
                </Group>
              </Paper>
            ))}
          </SimpleGrid>
        ) : (
          <Center py="lg">
            <Stack align="center" gap="xs">
              <ThemeIcon variant="light" size={48} radius="xl" color="produce">
                <IconListCheck size={24} />
              </ThemeIcon>
              <Text fw={600}>Personalized suggestions coming soon</Text>
              <Text c="dimmed" size="sm" ta="center" maw={300}>
                Once you have a few orders, we'll suggest items and quantities tailored to your buying patterns.
              </Text>
              <Button component={Link} to="/shop" variant="light" color="produce" mt="xs" leftSection={<IconShoppingCart size={16} />}>
                Start Shopping
              </Button>
            </Stack>
          </Center>
        )}
      </Paper>

      {/* Recent Orders */}
      <Paper p="lg" radius="md">
        <Group justify="space-between" mb="md">
          <Group gap="xs">
            <ThemeIcon variant="light" size="md" radius="xl" color="brand">
              <IconClipboardList size={18} />
            </ThemeIcon>
            <Text fw={700} size="lg">Recent Orders</Text>
          </Group>
          <Button
            component={Link}
            to="/orders"
            variant="light"
            size="sm"
            rightSection={<IconArrowRight size={14} />}
          >
            View All
          </Button>
        </Group>
        {ordersLoading ? (
          <Stack gap="xs">
            {[...Array(3)].map((_, i) => (
              <Skeleton key={i} height={56} radius="md" />
            ))}
          </Stack>
        ) : !ordersData?.items?.length ? (
          <EmptyState
            title="No orders yet"
            description="Browse our catalog to place your first order"
          />
        ) : (
          <Stack gap="xs">
            {ordersData.items.map((order) => (
              <Paper
                key={order.id}
                withBorder
                p="sm"
                radius="md"
                style={{ transition: 'all 150ms ease', cursor: 'pointer' }}
                component={Link}
                to={`/orders/${order.id}`}
              >
                <Group justify="space-between" wrap="nowrap">
                  <Group gap="md" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
                    <ThemeIcon
                      variant="light"
                      size="lg"
                      radius="md"
                      color={
                        order.status === 'delivered' ? 'produce'
                          : order.status === 'cancelled' ? 'red'
                          : 'brand'
                      }
                    >
                      <IconPackage size={18} />
                    </ThemeIcon>
                    <div style={{ minWidth: 0 }}>
                      <Text size="sm" fw={600} c="brand" style={{ textDecoration: 'none' }}>
                        {order.order_number}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {dayjs(order.created_at).format('MMM D, YYYY')}
                      </Text>
                    </div>
                  </Group>
                  <Group gap="md" wrap="nowrap">
                    <Text size="sm" fw={600}>
                      ${parseFloat(order.total).toFixed(2)}
                    </Text>
                    <StatusBadge status={order.status} />
                  </Group>
                </Group>
              </Paper>
            ))}
          </Stack>
        )}
      </Paper>
    </Stack>
  );
}
